/**
 * Pure helpers for estimating per-translate token cost and remaining
 * translates in the user's daily quota. Shared between API (canSpend
 * pre-flight) and Web (live quota indicator).
 *
 * Everything here is dependency-free bookkeeping math, so it's trivially
 * testable. Exact tokenization stays on the server, which owns the real
 * gpt-tokenizer and the numbers that quota enforcement acts on.
 */
export const FRAMING_OVERHEAD = 25;
export const EMPTY_INPUT_DEFAULT_TOKENS = 5;
export const OUTPUT_MULTIPLIER = 2;
// Average characters per BPE token, by script. ASCII prose packs several
// characters into one token; Cyrillic and other non-Latin scripts fall back to
// shorter, byte-level pieces and cost roughly one token per two characters.
export const ASCII_CHARS_PER_TOKEN = 5;
export const WIDE_CHARS_PER_TOKEN = 2;
export function estimateTokensPerTranslate(args) {
    const framing = args.framingOverhead ?? FRAMING_OVERHEAD;
    const mult = args.outputMultiplier ?? OUTPUT_MULTIPLIER;
    return args.systemPromptTokens + framing + args.inputTokens + mult * args.inputTokens;
}
export function estimateTranslatesLeft(remainingTokens, tokensPerTranslate) {
    if (tokensPerTranslate <= 0 || remainingTokens <= 0)
        return 0;
    return Math.floor(remainingTokens / tokensPerTranslate);
}
/**
 * Approximates the BPE token count of `text` by script mix, without shipping a
 * tokenizer. Loading a real one costs ~450 KB gzipped for a number that only
 * feeds the "~N tokens per translate" hint — a hint already dominated by the
 * server-supplied system-prompt count, and already multiplied by a guessed
 * output factor. Enforcement never uses this: the server tokenizes exactly.
 */
export function estimateTokenCount(text) {
    let ascii = 0;
    let wide = 0;
    for (const char of text) {
        if ((char.codePointAt(0) ?? 0) < 128)
            ascii++;
        else
            wide++;
    }
    // Rounding up means any non-empty string costs at least one token.
    return Math.ceil(ascii / ASCII_CHARS_PER_TOKEN + wide / WIDE_CHARS_PER_TOKEN);
}
/**
 * Token cost of the user's input, falling back to a small constant for blank
 * input so the indicator doesn't read as free. `countTokens` is injectable so
 * the estimate can be calibrated against a real tokenizer in tests.
 */
export function effectiveInputTokens(text, countTokens = estimateTokenCount) {
    return text.trim() ? countTokens(text) : EMPTY_INPUT_DEFAULT_TOKENS;
}
